//  ----------      START   IMPORT  BLOCK       ----------
import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from "react";
//  Widgets
import {Accordion, Button, Card, Col, Form, Row, OverlayTrigger, Tooltip} from "react-bootstrap";
//  Context
import { useLanguage } from "../../../API/Context/LanguageContext";
//  Config
import VocabWorkingHours from "../../../ConfigFiles/multilingual/VocabWorkingHours";
import {VocabButtons as btn_vocab} from "../../../ConfigFiles/multilingual/VocabButtons";
import { get_weekdays } from "../../../ConfigFiles/multilingual/VocabWeekdays";
//  Icons
import { CheckCircleFill, XCircleFill, InfoCircle, ExclamationTriangle } from "react-bootstrap-icons";
//  Helper functions
import { formatTime,buildDateTime, validateSchedule } from "../../../Utils/timeUtils.ts";
//  ----------      END   IMPORT  BLOCK       ----------

const WorkingHoursForm = forwardRef(({ initialData = [], onChange }, ref) => {
      //  Consume context
      const { lan } = useLanguage();

    const daysOfWeek = get_weekdays(lan) ?? [];
    const safeDays = daysOfWeek.length === 7
        ? daysOfWeek
        : ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

    /**
     * Initializes the component's state schedule from API data.
     */
    const initializeSchedule = (data) => {
        const weeklyData = Array.isArray(data) && data.length === 7 ? data : Array.from({ length: 7 }, () => []);

        return weeklyData.map((dayIntervals) => {
            const hasIntervals = Array.isArray(dayIntervals) && dayIntervals.length > 0;
            return {
                working: hasIntervals,
                intervals: hasIntervals
                    ? dayIntervals.map((it) => ({
                        // *** USING IMPORTED formatTime ***
                        start: formatTime(it.start),
                        end: formatTime(it.end),
                    }))
                    : [{ start: "", end: "" }],
            };
        });
    };

    // State Initialization
    const [schedule, setSchedule] = useState(initializeSchedule(initialData));
    const [manageAgenda, setManageAgenda] = useState(true);
    const [scheduleVisible, setScheduleVisible] = useState(true);

    // State for validation results (using the imported validateSchedule)
    const [validation, setValidation] = useState(() => validateSchedule(initializeSchedule(initialData)));

    const lastSubmittedDataRef = useRef(null);


    // 1. Validation and Parent Notification
    useEffect(() => {
        // *** USING IMPORTED validateSchedule ***
        const validationResult = validateSchedule(schedule);
        setValidation(validationResult);

        // Structure the data for the parent component (RegisterProfessionalPage)
        let newData = null;

        if (validationResult.isOverallValid) {
            const weekly_schedule = schedule.map((day) =>
                day.working
                    ? day.intervals.filter(i => i.start && i.end).map(i => ({
                        // *** USING IMPORTED buildDateTime ***
                        start: buildDateTime(i.start),
                        end: buildDateTime(i.end)
                    }))
                    : []
            );

            // Valid structured data
            newData = {
                manage_agenda: manageAgenda,
                schedule_visible: scheduleVisible,
                weekly_schedule: weekly_schedule
            };
        }

        // FIX: CRITICAL DEEP COMPARISON CHECK
        if (JSON.stringify(newData) !== JSON.stringify(lastSubmittedDataRef.current)) {
            lastSubmittedDataRef.current = newData;
            onChange?.(newData);
        }

    }, [schedule, manageAgenda, scheduleVisible, onChange]);

    // 2. Expose Data to Parent via ref (for SlidingWidgetMenu)
    useImperativeHandle(ref, () => ({
        getData: () => {
            if (!validation.isOverallValid) {
                // Return null if data is invalid, preventing API call
                return null;
            }

            // Format data for API update (in snake_case)
            return {
                manage_agenda: manageAgenda,
                schedule_visible: scheduleVisible,
                weekly_schedule: schedule.map((day) =>
                    day.working
                        ? day.intervals.filter(i => i.start && i.end).map(i => ({
                              // *** USING IMPORTED buildDateTime ***
                              start: buildDateTime(i.start),
                              end: buildDateTime(i.end)
                          }))
                        : []
                ),
            };
        },
    }));

    // --- Handlers ---
    const handleToggleDay = (dayIdx) => {
        setSchedule(prev =>
            prev.map((d, i) => i === dayIdx ? { ...d, working: !d.working } : d)
        );
    };

    const handleIntervalChange = (dayIdx, intervalIdx, field, value) => {
        setSchedule(prev =>
            prev.map((d, i) =>
                i === dayIdx
                    ? {
                        ...d,
                        intervals: d.intervals.map((slot, j) =>
                            j === intervalIdx ? { ...slot, [field]: value } : slot
                        )
                    }
                    : d
            )
        );
    };

    const addInterval = (dayIdx) => {
        setSchedule(prev =>
            prev.map((d, i) =>
                i === dayIdx
                    ? { ...d, intervals: [...d.intervals, { start: "", end: "" }] }
                    : d
            )
        );
    };

    const removeInterval = (dayIdx, intervalIdx) => {
        setSchedule(prev =>
            prev.map((d, i) =>
                i === dayIdx
                    ? { ...d, intervals: d.intervals.filter((_, j) => j !== intervalIdx) }
                    : d
            )
        );
    };

    const renderTooltip = (text) => <Tooltip id="info-tooltip">{text}</Tooltip>;

    // --- Render ---
    return (
        <Card className="p-3 shadow-sm">
            <h4 className="mb-3">{VocabWorkingHours.working_hours[lan]}</h4>

            {/* Global switches */}
            <Row className="mb-3">
                <Col xs={12} md={6}>
                    <Form.Check
                        type="switch"
                        label={
                            <span>
                                {VocabWorkingHours.manage_my_agenda[lan]}
                                <OverlayTrigger placement="right" overlay={renderTooltip(VocabWorkingHours.manage_my_agenda_hint[lan])}>
                                    <InfoCircle className="ms-1 text-primary" />
                                </OverlayTrigger>
                            </span>
                        }
                        checked={manageAgenda}
                        onChange={(e) => setManageAgenda(e.target.checked)}
                    />
                </Col>
                <Col xs={12} md={6}>
                    <Form.Check
                        type="switch"
                        label={
                            <span>
                                {VocabWorkingHours.make_schedule_visible[lan]}
                                <OverlayTrigger placement="right" overlay={renderTooltip(VocabWorkingHours.make_schedule_visible_hint[lan])}>
                                    <InfoCircle className="ms-1 text-primary" />
                                </OverlayTrigger>
                            </span>
                        }
                        checked={scheduleVisible}
                        onChange={(e) => setScheduleVisible(e.target.checked)}
                    />
                </Col>
            </Row>

            <Accordion alwaysOpen>
                {safeDays.map((day, dayIdx) => {
                    const isDayInvalid = validation.detailedErrors[dayIdx].hasErrors;

                    return (
                        <Accordion.Item eventKey={dayIdx.toString()} key={`${day}-${dayIdx}`}>
                            <Accordion.Header>
                                {/* Status Icon: Success, Fail, or Error */}
                                {schedule[dayIdx].working && isDayInvalid ? (
                                    <ExclamationTriangle className="text-warning me-2" />
                                ) : schedule[dayIdx].working ? (
                                    <CheckCircleFill className="text-success me-2" />
                                ) : (
                                    <XCircleFill className="text-danger me-2" />
                                )}
                                {day}
                            </Accordion.Header>
                            <Accordion.Body>
                                <Form.Check
                                    type="switch"
                                    label={VocabWorkingHours.workable_day[lan]}
                                    checked={schedule[dayIdx].working}
                                    onChange={() => handleToggleDay(dayIdx)}
                                />
                                {schedule[dayIdx].working &&
                                    schedule[dayIdx].intervals.map((interval, intervalIdx) => {
                                        // Get specific error flags for this interval
                                        const intervalErrors = validation.detailedErrors[dayIdx].intervals[intervalIdx];

                                        // Apply red border style if there's an error
                                        const errorStyle = { borderColor: 'red', borderWidth: '2px' };

                                        return (
                                            <Row className="mb-2 align-items-center" key={`${dayIdx}-${intervalIdx}`}>
                                                <Col>
                                                    <Form.Control
                                                        type="time"
                                                        value={interval.start}
                                                        onChange={(e) => handleIntervalChange(dayIdx, intervalIdx, "start", e.target.value)}
                                                        style={intervalErrors?.startError ? errorStyle : {}}
                                                    />
                                                </Col>
                                                <Col>
                                                    <Form.Control
                                                        type="time"
                                                        value={interval.end}
                                                        onChange={(e) => handleIntervalChange(dayIdx, intervalIdx, "end", e.target.value)}
                                                        style={intervalErrors?.endError ? errorStyle : {}}
                                                    />
                                                </Col>
                                                <Col xs="auto">
                                                    <Button
                                                        variant="outline-danger" size="sm"
                                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeInterval(dayIdx, intervalIdx); }}
                                                        disabled={schedule[dayIdx].intervals.length === 1}
                                                    >
                                                        {btn_vocab.remove[lan]}
                                                    </Button>
                                                </Col>
                                            </Row>
                                        );
                                    })}
                                {schedule[dayIdx].working && (
                                    <Button
                                        type="button" variant="outline-primary" size="sm"
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); addInterval(dayIdx); }}
                                    >
                                        {btn_vocab.add[lan]}
                                    </Button>
                                )}
                            </Accordion.Body>
                        </Accordion.Item>
                    );
                })}
            </Accordion>
            {!validation.isOverallValid && (
                <div className="mt-3 p-2 border border-danger text-danger rounded">
                    <ExclamationTriangle className="me-2" />
                    <strong>{VocabWorkingHours.validation_error_label[lan]}:</strong> {VocabWorkingHours.validation_error_message[lan]}
                </div>
            )}
        </Card>
    );
});

export default WorkingHoursForm;